// Login form. POSTs to Payload's /api/users/login via the api client;
// on success the auth store fires and the router guard redirects to
// the original target (or /dashboard).

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { BrandMark } from '@/components/Brand';

export function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  // The route declares { redirect?: string } as a search param schema;
  // useSearch infers that shape.
  const search = useSearch({ from: '/login' as never }) as { redirect?: string };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    void navigate({ to: (search.redirect ?? '/dashboard') as never, replace: true });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      await navigate({ to: (search.redirect ?? '/dashboard') as never, replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 401 ? 'Invalid email or password.' : err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--bo-bg)] px-4 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-[color:var(--bo-line)] bg-white p-8 shadow-sm">
        <BrandMark />
        <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Sign in</h1>
        <p className="mt-1 text-[13px] text-[color:var(--bo-mute)]">
          Use your DriveCars admin email + password.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-[12px] font-medium text-[color:var(--bo-ink)]">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[14px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-[12px] font-medium text-[color:var(--bo-ink)]">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[14px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </div>

          {error ? (
            <div
              role="alert"
              className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-[color:var(--bo-ink)] text-[13.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center text-[11.5px] text-[color:var(--bo-mute)]">
          <Link
            to={'/forgot-password' as never}
            className="font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            Forgot your password?
          </Link>
        </p>
      </div>
    </div>
  );
}
